import React, { createContext, type FC, type PropsWithChildren, useContext, useEffect, useRef } from 'react'

import { useTableSelectedIds } from '../../common'

type BulkModalScope = Readonly<{
    /**
     * - `list`: the modals of a list whose record is open in a panel
     * - `panel`: the modals of the record open in a panel
     */
    owner: 'list' | 'panel'
    /** The record open in the panel, as far as its modals are mounted */
    openId: string | null
    /** If the list's selection is that record alone, so an action on it alone comes from the list's toolbar */
    openRecordSelected: boolean
}>

const BulkModalScopeContext = createContext<BulkModalScope | null>(null)

/**
 * A list and the record open over it in a panel both mount modals for the same actions; the scope gives each action to one of them:
 * the panel takes an action on its record alone, unless that's the list's selection, whose modals then clear it once done
 */
export const BulkModalScopeProvider: FC<BulkModalScope & PropsWithChildren> = ({ owner, openId, openRecordSelected, children }) => (
    <BulkModalScopeContext.Provider value={{ owner, openId, openRecordSelected }}>{children}</BulkModalScopeContext.Provider>
)

/**
 * The scope of the list's modals, rendered inside its table to read its selection, which it tells the panel about
 */
export const ListBulkModalScope: FC<Readonly<{ openId: string | null, onOpenRecordSelected: (selected: boolean) => void }> & PropsWithChildren> = ({
    openId,
    onOpenRecordSelected,
    children,
}) => {
    const [selected, ...more] = useTableSelectedIds()
    const openRecordSelected = openId !== null && more.length === 0 && selected === openId

    useEffect(() => onOpenRecordSelected(openRecordSelected), [onOpenRecordSelected, openRecordSelected])

    return (
        <BulkModalScopeProvider owner="list" openId={openId} openRecordSelected={openRecordSelected}>
            {children}
        </BulkModalScopeProvider>
    )
}

const isOwned = (scope: BulkModalScope | null, ids: readonly string[], underWayOnMount: boolean): boolean => {
    if (scope === null) {
        return true
    }

    const { owner, openId, openRecordSelected } = scope
    const isPanelAction = openId !== null && !openRecordSelected && ids.length === 1 && ids[0] === openId

    switch (owner) {
        case 'list':
            return !isPanelAction
        case 'panel':
            /** An action already under way when the panel's modals mount was the list's */
            return isPanelAction && !underWayOnMount
    }
}

/**
 * @returns if the modals in this scope act on the action on the given ids (`null` while there is none); outside a scope, they always do
 *
 * Decided once per action, as its ids are set (selecting sets a new array, its progress keeps it), so live data (the list's rows,
 * its selection, the panel leaving) can't hand an open dialog over to the other modal
 */
export const useIsBulkActionOwned = (ids: readonly string[] | null): boolean => {
    const scope = useContext(BulkModalScopeContext)
    const decision = useRef<Readonly<{ ids: readonly string[], owned: boolean }> | null>(null)
    const mounted = useRef(false)

    if (ids === null) {
        decision.current = null
    } else if (decision.current?.ids !== ids) {
        decision.current = { ids, owned: isOwned(scope, ids, !mounted.current) }
    }

    useEffect(() => {
        mounted.current = true
    }, [])

    return decision.current?.owned ?? true
}
